import { useEffect, useState } from "react";
import { Link, useSearchParams } from "react-router";
import { Pagination } from "../components/Pagination";
import {
  Alert,
  Card,
  EmptyState,
  TableSkeleton,
  Mono,
  PageHeader,
  Select,
  Table,
  Td,
  Th,
  buttonStyles,
} from "../components/ui";
import { formatDateTime, formatRelative, humanizeAction } from "../lib/format";
import { useAudit } from "../lib/queries";

const LIMIT = 50;

export function AuditPage() {
  const [scope, setScope] = useState<"all" | "platform">("all");
  const [offset, setOffset] = useState(0);
  const [searchParams] = useSearchParams();
  const orgId = searchParams.get("orgId") || undefined;
  const actorUserId = searchParams.get("actorUserId") || undefined;
  const context =
    orgId && actorUserId
      ? "Showing the selected organization and actor."
      : orgId
        ? "Showing one organization."
        : actorUserId
          ? "Showing one user's actions."
          : null;

  // Context links are allowed to arrive from browser history as well as a page
  // action. A later offset from the previous investigation can be empty here.
  useEffect(() => setOffset(0), [orgId, actorUserId]);

  const { data, isPending, error } = useAudit({
    limit: LIMIT,
    offset,
    ...(orgId ? { orgId } : {}),
    ...(actorUserId ? { actorUserId } : {}),
    ...(scope === "platform" ? { platformOnly: true } : {}),
  });

  return (
    <>
      <PageHeader
        title="Audit log"
        description="Every recorded action, from tenants and from platform staff."
        action={
          <Select
            value={scope}
            onChange={(e) => {
              setScope(e.target.value as "all" | "platform");
              setOffset(0);
            }}
            className="w-full sm:max-w-52"
            aria-label="Filter scope"
          >
            <option value="all">All activity</option>
            <option value="platform">Platform actions only</option>
          </Select>
        }
      />

      {context && (
        <div className="flex flex-col gap-3 border border-accent/30 bg-accent/8 px-4 py-3 sm:flex-row sm:items-center sm:justify-between">
          <div>
            <p className="micro text-accent">Investigation context</p>
            <p className="mt-1 text-sm text-fg">{context}</p>
          </div>
          <Link to="/audit" className={buttonStyles("secondary", "sm")}>
            Clear context
          </Link>
        </div>
      )}

      <Card>
        {isPending ? (
          <TableSkeleton columns={5} />
        ) : error ? (
          <div className="p-5">
            <Alert>{error.message}</Alert>
          </div>
        ) : data.data.length === 0 ? (
          <EmptyState title="Nothing recorded yet" />
        ) : (
          <>
            <Table
              head={
                <tr>
                  <Th>When</Th>
                  <Th>Action</Th>
                  <Th priority="sm">Actor</Th>
                  <Th priority="md">Organization</Th>
                  <Th priority="lg">Target</Th>
                </tr>
              }
            >
              {data.data.map((entry) => (
                <tr key={entry.id} className="hover:bg-raised">
                  <Td title={formatDateTime(entry.createdAt)} className="whitespace-nowrap">
                    {formatRelative(entry.createdAt)}
                  </Td>
                  <Td className="font-medium">
                    {humanizeAction(entry.action)}
                    {/* Actor and tenant remain available beside the action on a
                        phone, where their desktop columns intentionally drop. */}
                    <p className="mt-0.5 text-xs font-normal text-fg-subtle sm:hidden">
                      {entry.actor?.name ?? "System"}
                      <span className="mx-1 text-line-strong">·</span>
                      {entry.organization?.name ?? "Platform"}
                    </p>
                    <p className="mt-0.5 hidden text-xs font-normal text-fg-subtle sm:block md:hidden">
                      {entry.organization?.name ?? "Platform"}
                    </p>
                  </Td>
                  <Td priority="sm">
                    {entry.actor ? (
                      <>
                        {entry.actor.name}
                        {entry.actor.email && (
                          <p className="text-xs text-fg-subtle">
                            {entry.actor.email}
                          </p>
                        )}
                      </>
                    ) : (
                      <span className="text-fg-subtle">System</span>
                    )}
                  </Td>
                  <Td priority="md">
                    {entry.organization ? (
                      <Link
                        to={`/organizations/${entry.organization.id}`}
                        className="hover:underline"
                      >
                        {entry.organization.name ?? entry.organization.id}
                      </Link>
                    ) : (
                      <span className="text-fg-subtle">Platform</span>
                    )}
                  </Td>
                  <Td className="text-fg-subtle" priority="lg">
                    {entry.target ? (
                      <>
                        <span className="capitalize">{entry.target.type}</span>
                        {entry.target.id && (
                          <Mono className="ml-2 text-fg-subtle">
                            {entry.target.id.slice(0, 8)}
                          </Mono>
                        )}
                      </>
                    ) : (
                      "—"
                    )}
                  </Td>
                </tr>
              ))}
            </Table>

            {/* `hasMore` rather than a total: the audit endpoint deliberately
                does not count, because the table grows without bound and a
                COUNT(*) over it would get slower forever. */}
            <Pagination
              hasMore={data.hasMore}
              limit={data.limit}
              offset={data.offset}
              onChange={setOffset}
            />
          </>
        )}
      </Card>
    </>
  );
}
